// Thin fetch wrapper: same-origin JSON API, cookie session, CSRF header on every call.

export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, body: unknown, message: string) {
        super(message);
        this.status = status;
        this.body = body;
    }
}

export function isUnauthorized(error: unknown): boolean {
    return error instanceof ApiError && error.status === 401;
}

// App registers a handler so any 401 mid-session flips the UI to the sign-in state.
let unauthorizedHandler: (() => void) | null = null;

export function onUnauthorized(handler: () => void): () => void {
    unauthorizedHandler = handler;
    return () => {
        if (unauthorizedHandler === handler) unauthorizedHandler = null;
    };
}

function errorMessage(status: number, body: unknown): string {
    if (body && typeof body === "object") {
        const err = (body as Record<string, unknown>).error;
        if (typeof err === "string") return err;
    }
    return `HTTP ${status}`;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
    const res = await fetch(path, { credentials: "include", ...init });
    let body: unknown = null;
    const text = await res.text();
    if (text) {
        try {
            body = JSON.parse(text);
        } catch {
            body = text;
        }
    }
    if (res.status === 401) {
        unauthorizedHandler?.();
        throw new ApiError(401, body, "not authenticated");
    }
    if (!res.ok) throw new ApiError(res.status, body, errorMessage(res.status, body));
    return body as T;
}

export function apiGet<T>(path: string): Promise<T> {
    return request<T>(path, { headers: { "X-Requested-With": "fetch" } });
}

export function apiPost<T>(path: string, payload?: unknown): Promise<T> {
    return request<T>(path, {
        method: "POST",
        headers: { "X-Requested-With": "fetch", "Content-Type": "application/json" },
        body: JSON.stringify(payload ?? {}),
    });
}
